"use client";

import type React from "react";
import { type ReactNode } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils/index";
import { FetchButton } from "@/fetch-ui/FetchButton";

type FetchEmptyStateAction = {
  /** Button label text */
  label: string;
  /** Button variant. Defaults to "outline" (existing behavior). */
  variant?: "outline" | "ghost";
  /** Hide the leading plus icon. Defaults to false (icon shown). */
  hideIcon?: boolean;
} & (
  | {
      /** Click handler for the action button */
      onClick: () => void;
      href?: never;
    }
  | {
      /** Renders the action as a link to this URL instead of a click handler */
      href: string;
      onClick?: never;
    }
);

interface FetchEmptyStateProps {
  /** Primary message, e.g. "No forms yet" */
  title: string;
  /** Secondary message, e.g. "Create a form to get started" */
  subtitle?: string;
  /** Optional action button rendered below the text */
  action?: FetchEmptyStateAction;
  /** Optional second action rendered beside `action`, e.g. a create CTA next to "Clear filters" */
  secondaryAction?: FetchEmptyStateAction;
  /** Optional icon rendered above the title */
  icon?: ReactNode;
  /** Optional className override for the outer container */
  className?: string;
}

/**
 * Dashed-border empty state placeholder with optional subtitle and up to two action buttons.
 * Used when a list or grid has no items to display.
 *
 * @param props - See {@link FetchEmptyStateProps}.
 * @returns The empty state placeholder.
 */
export function FetchEmptyState({
  title,
  subtitle,
  action,
  secondaryAction,
  icon,
  className,
}: FetchEmptyStateProps): React.ReactElement {
  const actions = [action, secondaryAction].filter((item): item is FetchEmptyStateAction => item !== undefined);

  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-16",
        className
      )}
    >
      {icon && <div className="mb-3">{icon}</div>}
      <p className="text-sm font-medium text-gray-700">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-center text-xs font-medium text-gray-500">{subtitle}</p>}
      {actions.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {actions.map((item) => (
            <FetchButton
              key={item.label}
              variant={item.variant ?? "outline"}
              size="sm"
              leftIcon={item.hideIcon ? undefined : <Plus className="h-3.5 w-3.5" />}
              onClick={item.onClick}
              href={item.href}
            >
              {item.label}
            </FetchButton>
          ))}
        </div>
      )}
    </div>
  );
}
